import { useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { jobsApi, type JobStatus } from '@/lib/api-client';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';

interface UseJobPollingOptions {
  jobId: number | null;
  enabled?: boolean;
  onComplete?: (storyId: string) => void;
  onError?: (error: string) => void;
  pollInterval?: number; // milliseconds
}

export function useJobPolling({
  jobId,
  enabled = true,
  onComplete,
  onError,
  pollInterval = 15000, // 15 seconds default
}: UseJobPollingOptions) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const completedRef = useRef(false);

  const { data: job, isLoading } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => jobsApi.getJobStatus(jobId!),
    enabled: enabled && jobId !== null && !completedRef.current,
    refetchInterval: (data) => {
      // Stop polling if job is completed or failed
      if (data?.status === 'completed' || data?.status === 'failed' || data?.status === 'cancelled') {
        return false;
      }
      return pollInterval;
    },
    refetchIntervalInBackground: true, // Keep polling even when tab is not focused
  });

  // Handle job completion
  useEffect(() => {
    if (!job || completedRef.current) return;

    if (job.status === 'completed') {
      completedRef.current = true;
      const storyId = job.result_data?.job_output?.story_id;
      const storyTitle = job.result_data?.job_output?.title;

      if (storyId) {
        // Show success notification
        notifications.show({
          id: `story-complete-${jobId}`,
          title: 'Story Ready!',
          message: storyTitle ? `"${storyTitle}" is ready to read` : 'Your story is ready',
          color: 'teal',
          autoClose: false,
          onClick: () => {
            navigate(`/story/${storyId}`);
            notifications.hide(`story-complete-${jobId}`);
          },
        });

        // Invalidate library to show new story
        queryClient.invalidateQueries({ queryKey: ['library'] });
        queryClient.invalidateQueries({ queryKey: ['stories'] });

        onComplete?.(storyId);
      }
    } else if (job.status === 'failed') {
      completedRef.current = true;
      const errorMessage = job.result_data?.error_details?.error_message || 'Story generation failed';

      notifications.show({
        title: 'Generation Failed',
        message: errorMessage,
        color: 'red',
      });

      onError?.(errorMessage);
    }
  }, [job, jobId, onComplete, onError, navigate, queryClient]);

  return {
    job,
    isLoading,
    isPolling: job?.status === 'pending' || job?.status === 'in_progress',
    isCompleted: job?.status === 'completed',
    isFailed: job?.status === 'failed',
  };
}
